import { Box, TextField, Typography } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import TimeField from "../../../common/components/TimeField/TimeField";
import { CustomButton } from "../../../common/components/functional/customButton/CustomButton";
import { SpinnerButton } from "../../../common/components/functional/spinnerButton/SpinnerButton";
import {
  combineDateAndTime,
  isUntilAfterFrom,
} from "../../../common/helpers/formatDateTime";
import {
  ExpertOutOfOffice,
  ExpertOutOfOfficePeriod,
} from "../../user/types/entities";
import {
  hasStatusMessage,
  mapFormValuesToOutOfOffice,
  mapOutOfOfficeToFormValues,
  StatusMessageFormValues,
} from "./statusMessageMappers";

type Props = {
  outOfOffice?: ExpertOutOfOffice | null;
  isLoading: boolean;
  title?: string;
  disableDatePickerPortal?: boolean;
  onCancel: () => void;
  onSave: (outOfOffice: ExpertOutOfOfficePeriod) => void;
  onClear?: () => void;
};

const datePickerSlotProps = {
  popper: {
    disablePortal: true,
    sx: { zIndex: 2000 },
    modifiers: [
      { name: "preventOverflow", enabled: false },
      { name: "flip", enabled: true },
    ],
  },
};

const StatusMessageForm = ({
  outOfOffice,
  isLoading,
  title,
  disableDatePickerPortal = true,
  onCancel,
  onSave,
  onClear,
}: Props) => {
  const { t } = useTranslation();
  const { control, handleSubmit, trigger, watch } =
    useForm<StatusMessageFormValues>({
      defaultValues: mapOutOfOfficeToFormValues(outOfOffice),
      mode: "onTouched",
    });

  const values = watch();
  const from = combineDateAndTime(values.fromDate, values.fromTime);
  const until = combineDateAndTime(values.untilDate, values.untilTime);
  const canSave = Boolean(from && until && isUntilAfterFrom(from, until));
  const showUntilOrderError = !isUntilAfterFrom(from, until);
  const datePickerSlots = disableDatePickerPortal
    ? datePickerSlotProps
    : undefined;
  const hasExistingPeriod = hasStatusMessage(outOfOffice);
  const untilAfterFromMessage = t("common:statusMessageUntilMustBeAfterFrom");
  const timePlaceholder = t("common:timePlaceholder");

  const isFormUntilAfterFrom = (formValues: StatusMessageFormValues) =>
    isUntilAfterFrom(
      combineDateAndTime(formValues.fromDate, formValues.fromTime),
      combineDateAndTime(formValues.untilDate, formValues.untilTime),
    );

  useEffect(() => {
    void trigger(["untilDate", "untilTime"]);
  }, [
    values.fromDate,
    values.fromTime,
    values.untilDate,
    values.untilTime,
    trigger,
  ]);

  return (
    <Box px={2} py={1} onKeyDown={(event) => event.stopPropagation()}>
      <Typography fontWeight="600" mb={1.5} variant="header">
        {title ?? t("common:setStatusMessage")}
      </Typography>
      <Controller
        control={control}
        name="message"
        render={({ field }) => (
          <TextField
            {...field}
            sx={{ mb: 1 }}
            disabled={isLoading}
            fullWidth
            maxRows={5}
            minRows={5}
            multiline
            placeholder={t("common:statusMessagePlaceholder")}
            size="small"
          />
        )}
      />
      <Typography color="text.secondary" mb={0.5}>
        {t("common:statusMessageFrom")}
      </Typography>
      <Box display="flex" gap={1} mb={1}>
        <Controller
          control={control}
          name="fromDate"
          rules={{ required: true }}
          render={({ field, fieldState }) => (
            <DesktopDatePicker
              {...field}
              disabled={isLoading}
              format="dd.MM.yyyy"
              slotProps={{
                ...datePickerSlots,
                textField: {
                  size: "small",
                  fullWidth: true,
                  error: fieldState.isTouched && !!fieldState.error,
                },
              }}
            />
          )}
        />
        <Controller
          control={control}
          name="fromTime"
          rules={{
            required: true,
            validate: (value, formValues) =>
              Boolean(combineDateAndTime(formValues.fromDate, value)),
          }}
          render={({ field, fieldState }) => (
            <TimeField
              disabled={isLoading}
              error={fieldState.isTouched && !!fieldState.error}
              onBlur={field.onBlur}
              onChange={field.onChange}
              placeholder={timePlaceholder}
              value={field.value}
            />
          )}
        />
      </Box>
      <Typography color="text.secondary" mb={0.5}>
        {t("common:statusMessageUntil")}
      </Typography>
      <Box display="flex" gap={1} mb={3}>
        <Controller
          control={control}
          name="untilDate"
          rules={{
            required: true,
            validate: (_, formValues) =>
              isFormUntilAfterFrom(formValues) || untilAfterFromMessage,
          }}
          render={({ field, fieldState }) => (
            <DesktopDatePicker
              {...field}
              disabled={isLoading}
              format="dd.MM.yyyy"
              slotProps={{
                ...datePickerSlots,
                textField: {
                  size: "small",
                  fullWidth: true,
                  error:
                    showUntilOrderError ||
                    (fieldState.isTouched &&
                      fieldState.error?.type === "required"),
                },
              }}
            />
          )}
        />
        <Controller
          control={control}
          name="untilTime"
          rules={{
            required: true,
            validate: (_, formValues) =>
              isFormUntilAfterFrom(formValues) || untilAfterFromMessage,
          }}
          render={({ field, fieldState }) => (
            <TimeField
              disabled={isLoading}
              error={
                showUntilOrderError ||
                (fieldState.isTouched && fieldState.error?.type === "required")
              }
              onBlur={field.onBlur}
              onChange={field.onChange}
              placeholder={timePlaceholder}
              value={field.value}
            />
          )}
        />
      </Box>
      <Box display="flex" justifyContent="space-between">
        <Box display="flex" gap={1}>
          <CustomButton
            color="primary"
            disabled={isLoading}
            onClick={onCancel}
            sx={{ borderRadius: "15px", height: "30px" }}
            type="button"
            variant="outlined"
          >
            {t("common:cancelShort")}
          </CustomButton>
          {onClear && hasExistingPeriod ? (
            <CustomButton
              color="error"
              disabled={isLoading}
              onClick={onClear}
              sx={{ borderRadius: "15px", height: "30px" }}
              type="button"
              variant="outlined"
            >
              {t("common:delete")}
            </CustomButton>
          ) : null}
        </Box>
        <SpinnerButton
          color="primary"
          disabled={!canSave || isLoading}
          label={t("common:save")}
          onClick={handleSubmit((formValues) =>
            onSave(mapFormValuesToOutOfOffice(formValues)),
          )}
          variant="contained"
        />
      </Box>
    </Box>
  );
};

export default StatusMessageForm;
